/**
 * Created by Ning Jiang on 6/5/2019.
 */

import {Button, ButtonState} from "@nolimitcity/slot-game/bin/core/component/Button";
import {IPickAndClickViewConfig, PickAndClickView} from "@nolimitcity/slot-game/bin/core/pickandclick/PickAndClickView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {Back, Power2, TimelineLite, TweenLite} from "gsap";
import {PPGameMode} from "../../gamemode/PPGameMode";
import {PixiesVsPiratesGameConfig} from "../../PixiesVsPiratesGameConfig";
import {IPixiesVsPiratesParsedServerData} from "../../server/parseddata/IPixiesVsPiratesParsedServerData";
import {PixiesVsPiratesInitialWinCountUp} from "../../winpresentation/initial/PixiesVsPiratesInitialWinCountUp";
import {FreespinOutroContainer} from "../FreespinOutroContainer";
import {PickButton} from "./PickButton";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {PixiesVsPirates} from "../../PixiesVsPirates";
import {PixiesVsPiratesParsedGameData} from "../../server/parseddata/PixiesVsPiratesParsedGameData";

export class PPPickNClickIntroView extends PickAndClickView {

    private readonly _buttonKeywords:string[] = ["pixie", "pirate"];
    // branch bottom-right/left corner to button center.
    private readonly _branchPos:[number, number][] = [[-481 + 612, -582 + 486], [-143, -577 + 466]];
    // button center to screen center.
    private readonly _bannerStartPos:[number, number][] = [[-969, -82], [957, 73]];
    private readonly _bannerEndPos:[number, number][] = [[-222, 212], [213, 212]];
    // text center to screen center.
    private readonly _bannerTextPos:[number, number][] = [[-227, -50], [207, -61]];
    private _playSoundCallback:() => void;
    private _playFeatureStartSoundCallback:() => void;

    private readonly _scrollTextStyle:TextStyleOptions = {
        fontFamily : "Open Sans",
        fontSize : 35,
        fill : "#2F0200",
        fontWeight : FontWeight.BOLD,
        align : "center",
        wordWrap : true,
        breakWords : true,
        wordWrapWidth : 600
    };

    private _stillBg:PIXI.Sprite;
    private _blurBg:PIXI.Sprite;
    private _outroInfoContainer:FreespinOutroContainer;
    private _gameContainer:PIXI.Container;
    private _buttonContainers:PIXI.Container[];
    private _buttonTexts:PIXI.Sprite[];
    private _scroll:PIXI.Container;
    private _scroolText:PIXI.Text;
    private _buttonPoof:TimelineSprite;
    private _buttonTextPoof:TimelineSprite;
    private _totalWinCountUp:PixiesVsPiratesInitialWinCountUp;

    constructor(config:IPickAndClickViewConfig) {
        super(config, false);
        this._totalWinCountUp = new PixiesVsPiratesInitialWinCountUp(PixiesVsPiratesGameConfig.instance.OUTRO_COUNTUP_BITMAP_STYLE);
    }

    protected createBackground():PIXI.DisplayObject {
        const bg:PIXI.Container = new PIXI.Container;

        const still:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("fsIntroBG")[0]);
        still.alpha = 0;
        still.visible = false;
        still.anchor.set(0.5);
        bg.addChild(still);
        this._stillBg = still;

        const blur:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("fsIntroBGBlur")[0]);
        blur.alpha = 0;
        blur.visible = false;
        blur.anchor.set(0.5);
        bg.addChild(blur);
        this._blurBg = blur;

        return bg;
    }

    protected onResizeBackground(data:IResizeData):void {
        // resize together with onResizeGameGraphics
    }

    protected createGameGraphics():PIXI.DisplayObject {
        this._outroInfoContainer = new FreespinOutroContainer();
        this._outroInfoContainer.totalWinCountUp = this._totalWinCountUp;
        this._outroInfoContainer.alpha = 0;
        this._outroInfoContainer.visible = false;
        this.addChild(this._outroInfoContainer);

        this._gameContainer = new PIXI.Container();

        this._scroll = new PIXI.Container;
        this._scroll.position.set(0, -284);
        this._scroll.alpha = 0;
        this._scroll.visible = false;
        this._gameContainer.addChild(this._scroll);

        const scrollBg:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("fsIntroTextScroll")[0]);
        scrollBg.anchor.set(0.5, 0.4);
        this._scroll.addChild(scrollBg);

        this._scroolText = new PIXI.Text("", this._scrollTextStyle);
        this._scroolText.anchor.set(0.5);
        this._scroolText.position.set(0, 6);
        this._scroll.addChild(this._scroolText);

        this._buttonContainers = [];
        this._buttonKeywords.forEach(() => {
            const buttonContainer:PIXI.Container = new PIXI.Container();
            buttonContainer.visible = false;
            this._gameContainer.addChild(buttonContainer);
            this._buttonContainers.push(buttonContainer);
        });

        this._buttonTexts = [];
        this._bannerTextPos.forEach((pos:[number, number], index:number) => {
            const text:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(`${this._buttonKeywords[index]}Text`)[0]);
            text.anchor.set(0.5);
            text.position.set(this._bannerTextPos[index][0], this._bannerTextPos[index][1]);
            text.alpha = 0;
            text.visible = false;
            this._gameContainer.addChild(text);
            this._buttonTexts.push(text);
        });

        this._buttonPoof = new TimelineSprite(GameResources.getTextures("bigWinPoof"));
        this._buttonPoof.blendMode = PIXI.BLEND_MODES.ADD;
        this._buttonPoof.anchor.set(0.5);
        this._buttonPoof.scale.set(4);
        this._buttonPoof.visible = false;
        this._gameContainer.addChild(this._buttonPoof);

        this._buttonTextPoof = new TimelineSprite(GameResources.getTextures("smallPoof"));
        this._buttonTextPoof.blendMode = PIXI.BLEND_MODES.ADD;
        this._buttonTextPoof.anchor.set(0.5);
        this._buttonTextPoof.scale.set(4);
        this._buttonTextPoof.visible = false;
        this._gameContainer.addChild(this._buttonTextPoof);

        return this._gameContainer;
    }

    protected onResizeGameGraphics(resizeData:IResizeData):void {
        const scale:number = Math.max(this._screenWidth / this._stillBg.texture.width, this._screenHeight / this._stillBg.texture.height);

        this._background.position.set(this._screenWidth / 2, this._screenHeight / 2);
        this._background.scale.set(scale, scale);

        this._gameContainer.position.set(this._screenWidth / 2, this._screenHeight / 2);

        this._outroInfoContainer.position.set(this._screenWidth / 2, this._screenHeight / 2);
        this._outroInfoContainer.updateElementsPosition(this._screenWidth, this._screenHeight, resizeData.orientation);

        if(resizeData.orientation === Orientation.PORTRAIT){
            this._gameContainer.scale.set(scale, scale);
            this._outroInfoContainer.scale.set(scale, scale);
        }else{
            const defaultScale:number = Math.max(PixiesVsPiratesGameConfig.instance.GAME_WIDTH/ this._stillBg.texture.width, PixiesVsPiratesGameConfig.instance.GAME_HEIGHT / this._stillBg.texture.height);
            this._gameContainer.scale.set(defaultScale);
            this._outroInfoContainer.scale.set(defaultScale);
        }

    }

    protected createButton(index:number, selectedIndex:number, clickCallback:(i:number, auto:boolean) => void):Button {
        const keyword:string = this._buttonKeywords[index];
        const button = new PickButton(keyword,
            () => {
                this.playClick(index);
                clickCallback(selectedIndex, this._autoClicked);
            });
        this._buttonContainers[index].addChild(button);

        const branch:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(`${keyword}Branch`)[0]);
        if(index === 0) {
            branch.anchor.set(1, 1);
        } else {
            branch.anchor.set(0, 1);
        }
        branch.position.set(this._branchPos[index][0], this._branchPos[index][1]);
        this._buttonContainers[index].addChild(branch);

        return button;
    }

    protected onResizeButtons(resizeData:IResizeData):void {
        // resize together with onResizeGameGraphics
    }

    protected startPresentation(data?:PixiesVsPiratesParsedGameData, onShowComplete?:() => void):void {
        const fsCount:number = data!.numberOfSpinsPerFSRound;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                const text:string = Translation.translate("Choose symbol to collect in ${fsCount} spins to retrigger Battle Rounds!");
                this._scroolText.text = text.replace("${fsCount}", fsCount.toString());
                this.playFeatureStartSound();
            },
            TweenLite.to(this._stillBg, 0.5, {alpha : 1, onStart : () => this._stillBg.visible = true}),
        ], 0); //  0-.5 sec
        timeline.add(TweenLite.to(this._blurBg, 0.5, {alpha : 1, onStart : () => this._blurBg.visible = true}), 0.5); // .5-1 sec
        if(data!.freespinRound > 0) {
            timeline.add(TweenLite.to(this._outroInfoContainer, 0.7, {
                alpha : 1,
                onStart : () => {
                    const tw:number = (data && data.accumulatedRoundWin)? data.accumulatedRoundWin : data!.totalWin;
                    const totalWinBelowStake: boolean = (PixiesVsPirates.api.gameClientConfiguration.belowStakeWinRestriction && (tw <= data!.playedBetValue));

                    this._outroInfoContainer.show(data!.totalWin, data!.numberOfFreespinsPlayed!, data!.pixieFSCount, data!.pirateFSCount, true, data!.playerFSSelection, totalWinBelowStake);
                    this._outroInfoContainer.visible = true;
                }
            }), 0.5);
            timeline.add("showButtons", "+=4");
            timeline.add(TweenLite.to(this._outroInfoContainer, 0.3, {
                alpha : 0,
                onComplete : () => {
                    this._outroInfoContainer.visible = false;
                    this._outroInfoContainer.close();
                }
            }), "showButtons");
        } else {
            timeline.add("showButtons");
        }
        timeline.add([
            () => this.playSound(),
            TweenLite.fromTo(this._buttonContainers[0], 0.5,
                {x : this._bannerStartPos[0][0], y : this._bannerStartPos[0][1]},
                {
                    x : this._bannerEndPos[0][0],
                    y : this._bannerEndPos[0][1],
                    ease : Power2.easeOut,
                    onStart : () => this._buttonContainers[0].visible = true
                })
        ], "showButtons"); // .5-1 sec
        timeline.add(TweenLite.fromTo(this._buttonContainers[1], 0.5,
            {x : this._bannerStartPos[1][0], y : this._bannerStartPos[1][1]},
            {
                x : this._bannerEndPos[1][0],
                y : this._bannerEndPos[1][1],
                ease : Power2.easeOut,
                onStart : () => this._buttonContainers[1].visible = true
            }), "showButtons+=0.2"); // .7-1.2 sec
        timeline.add([
            TweenLite.fromTo(this._scroll, 0.2, {alpha : 0}, {alpha : 1, onStart : () => this._scroll.visible = true}),
            TweenLite.fromTo(this._scroll.scale, 0.4,
                {x : 1.44, y : 1.44},
                {x : 1, y : 1, ease : Back.easeOut.config(1.5)}
            )
        ], "showButtons+=0.33"); // 25f
        timeline.add([
            TweenLite.fromTo(this._buttonTexts[0], 0.2,
                {alpha : 0},
                {
                    alpha : 1,
                    onStart : () => this._buttonTexts[0].visible = true
                }),
            TweenLite.fromTo(this._buttonTexts[1], 0.2,
                {alpha : 0},
                {
                    alpha : 1,
                    onStart : () => this._buttonTexts[1].visible = true
                }),
            TweenLite.fromTo(this._buttonTexts[0].scale, 0.25, {x : 1.6, y : 1.6}, {x : 0.95, y : 0.95}),
            TweenLite.fromTo(this._buttonTexts[1].scale, 0.25, {x : 1.6, y : 1.6}, {x : 0.95, y : 0.95}),
        ], "showButtons+=0.35"); // .85-1.05 sec
        timeline.add([
            TweenLite.to(this._buttonTexts[0].scale, 0.05, {x : 1, y : 1, ease : Back.easeOut.config(4)}),
            TweenLite.to(this._buttonTexts[1].scale, 0.05, {x : 1, y : 1, ease : Back.easeOut.config(4)}),
        ], "showButtons+=0.6"); // 1.10-1.15 sec

        timeline.add(() => super.startPresentation(data, onShowComplete));
        timeline.play();

        const isRestoreSelected:boolean = data!.mode === PPGameMode.FREESPIN_PICK;
        if(isRestoreSelected) {
            timeline.progress(1);
        }
    }

    private playClick(index:number):void {
        this._buttonPoof.position.set(this._buttonContainers[index].x, this._buttonContainers[index].y);
        this._buttonPoof.getAnimationAutoShowHide();
        this._buttonTextPoof.position.set(this._buttonTexts[index].x, this._buttonTexts[index].y);
        this._buttonTextPoof.getAnimationAutoShowHide();
        const buttonBounce:TimelineLite = new TimelineLite();
        buttonBounce.to(this._buttons[index].scale, 0.06, {x : 0.8, y : 0.8})
            .to(this._buttons[index].scale, 0.1, {x : 1, y : 1});
    }

    public wait():void {
        // TODO
    }

    protected stopPresentation(data?:IPixiesVsPiratesParsedServerData, closeCallback?:() => void):void {
        const timeline:TimelineLite = new TimelineLite();
        const selectedIndex:number = data!.playerFSSelection - 1;
        const unselectedIndex:number = selectedIndex ^ 1;
        if(selectedIndex < 0 || selectedIndex > 1) {
            debugger;
            throw new Error(`PPPickNClickIntroView.stopPresentation(): illegal data.playerFSSelection!`);
        }
        timeline.add([
            () => {
                (<PickButton>this._buttons[selectedIndex]).playGlow();
                (<PickButton>this._buttons[selectedIndex]).setState(ButtonState.idle);
            }
        ]);
        timeline.add([
            (<PickButton>this._buttons[unselectedIndex]).playUnselectedTint(),
            TweenLite.to(this._buttonTexts[selectedIndex].scale, 0.2, {x : 1.5, y : 1.5, ease : Back.easeIn.config(2)}),
            TweenLite.to(this._buttonTexts[unselectedIndex].scale, 0.1, {
                x : 0.14, y : 0.14,
                onComplete : () => this._buttonTexts[unselectedIndex].visible = false
            })
        ]);
        timeline.add(TweenLite.to(this._buttonTexts[selectedIndex], 0.5, {x : 0, y : 0}));

        timeline.add("close", "+=1");
        timeline.add([
            TweenLite.to(this._stillBg, 0.5, {alpha : 0, onComplete : () => this._stillBg.visible = false}),
            TweenLite.to(this._blurBg, 0.5, {alpha : 0, onComplete : () => this._blurBg.visible = false}),
            TweenLite.to(this._scroll, 0.4, {alpha : 0, onComplete : () => this._scroll.visible = false}),
            TweenLite.to(this._buttonPoof, 0.3, {
                alpha : 0,
                onComplete : () => {
                    this._buttonPoof.visible = false;
                    this._buttonPoof.alpha = 1;
                }
            }),
            TweenLite.to(this._buttonTextPoof, 0.3, {
                alpha : 0,
                onComplete : () => {
                    this._buttonTextPoof.visible = false;
                    this._buttonTextPoof.alpha = 1;
                }
            })
        ], "close");
        this._buttonContainers.forEach((buttonContainer:PIXI.Container, index:number) => {
            timeline.add(TweenLite.to(buttonContainer, 0.3, {
                alpha : 0,
                onComplete : () => {
                    buttonContainer.visible = false;
                    buttonContainer.alpha = 1;
                }
            }), "close");
        });
        this._buttonTexts.forEach((buttonText:PIXI.Sprite, index:number) => {
            timeline.add(TweenLite.to(buttonText, 0.3, {
                alpha : 0,
                onComplete : () => {
                    buttonText.visible = false;
                    buttonText.position.set(this._bannerTextPos[index][0], this._bannerTextPos[index][1]);
                }
            }), "close");
        });
        timeline.add(() => (<PickButton>this._buttons[unselectedIndex]).resetUnselecteTime());
        timeline.add(() => super.stopPresentation(data, closeCallback));
        timeline.play();
    }

    public getPlaySoundCallback(playSoundCallback:() => void):void {
        this._playSoundCallback = playSoundCallback;
    }

    public getFeatureStartSoundCallback(playSoundCallback:() => void):void {
        this._playFeatureStartSoundCallback = playSoundCallback;
    }

    private playFeatureStartSound():void {
        if(this._playFeatureStartSoundCallback) {
            this._playFeatureStartSoundCallback();
        }
    }

    private playSound():void {
        if(this._playSoundCallback) {
            this._playSoundCallback();
        }
    }
}
